import * as React from "react";

import { FAStringInput } from "@impero-com/common/components/form/formAtomFields/faStringInput";
import { formAtom } from "@impero-com/common/utils/form/formAtom";
import { useFormAtom } from "@impero-com/common/utils/form/formAtom/hooks";
import { FormAtom } from "@impero-com/common/utils/form/formAtom/types";
import { t } from "@impero-com/common/utils/i18n";
import { assertNever } from "@impero-com/common/utils/obj";
import { Popconfirm, Table, Typography } from "antd";
import type { ColumnsType } from "antd/es/table";
import {
    atom,
    PrimitiveAtom,
    SetStateAction,
    useAtom,
    useAtomValue,
    useSetAtom,
} from "jotai";
import { string, StringSchema } from "yup";

import * as theme from "./theme.css";

const MAX_TAG_NAME_LENGTH = 255;

interface TagManagementTableProps<Tag, TagId extends string | number> {
    // Already existing tags
    tags: Array<Tag>;

    // A utility function to an ID from a tag
    getTagId: (tag: Tag) => TagId;

    // A utility function to extract a name from a tag
    getTagName: (tag: Tag) => string;

    // A callback, which is invoked when tag is renamed
    onRename: (id: TagId, newTagName: string) => void;

    // A callback, which is invoked when one of the existing tags needs to be deleted
    onDelete: (id: TagId) => void;

    // A callback, which is invoked when a new tag needs to be added/created.
    onAdd: (tagName: string) => void;

    // Specifies if confirmation prompt needs to be display on tag deletion.
    // We want to ask confirmation when an existing category is being edited,
    // but it's pointless to ask confirmation when category does not exist yet.
    askConfirmationOnDelete: boolean;

    // Tag ids which should have actions disabled
    disabledIds?: Array<TagId>;
}

type DataRecord<TagId> =
    | ({ kind: "Tag" } & DataRecordTag<TagId>)
    | ({ kind: "New" } & DataRecordNew);

// A record for a row with an existing tag
interface DataRecordTag<TagId> {
    id: TagId;
    name: string;
    validationSchema: StringSchema<string>;
    fieldAtom: FormAtom<string>;
    disabled: boolean;
}

// A record for a row with input to add a new tag
interface DataRecordNew {
    //  ID don't belong here semantically, but we need it in order to feed it to antd's Table as rowKey.
    id: string;
    validationSchema: StringSchema<string>;
    fieldAtom: FormAtom<string>;
}

// Defines how a row must be rendered depending on its state
type RowState = "Display" | "Disabled" | "Editing";

function detectRowState<TagId>(
    record: DataRecordTag<TagId>,
    editingId: TagId | null
): RowState {
    if (record.id === editingId) {
        return "Editing";
    } else if (editingId === null) {
        return "Display";
    } else {
        return "Disabled";
    }
}

function TagManagementTable<Tag, TagId extends string | number>(
    props: TagManagementTableProps<Tag, TagId>
): React.ReactElement {
    const {
        tags,
        onRename,
        onDelete,
        onAdd,
        askConfirmationOnDelete,
        getTagId,
        getTagName,
        disabledIds = [],
    } = props;

    const tagNames = React.useMemo(() => {
        return tags.map(getTagName);
    }, [tags, getTagName]);

    // Reflects a tag which is being edited at the moment.
    const editingIdAtom: PrimitiveAtom<TagId | null> = React.useMemo(() => {
        return atom<TagId | null>(null);
    }, []);

    const addNewValidationSchema = React.useMemo(() => {
        return buildValidationSchema(tagNames, "");
    }, [tagNames]);

    const addNewFieldAtom = React.useMemo(() => {
        const addNewBaseAtom = atom(
            "",
            (get, _set, newTag: SetStateAction<string>) => {
                onAdd(
                    newTag instanceof Function
                        ? newTag(get(addNewBaseAtom))
                        : newTag
                );
            }
        );
        return formAtom(addNewBaseAtom, addNewValidationSchema);
    }, [addNewValidationSchema, onAdd]);

    const resetNew = useSetAtom(addNewFieldAtom.resetFieldAtom);

    const dataRecords: Array<DataRecord<TagId>> = React.useMemo(() => {
        // Existing tags
        const records: Array<DataRecord<TagId>> = tags.map((tag) => {
            // For every single input built its own dedicated validation schema.
            const name = getTagName(tag);
            const validationSchema = buildValidationSchema(tagNames, name);
            const baseAtom = atom(
                name,
                (get, set, newTagName: SetStateAction<string>) => {
                    onRename(
                        id,
                        newTagName instanceof Function
                            ? newTagName(get(baseAtom))
                            : newTagName
                    );
                    set(editingIdAtom, null);
                }
            );
            const fieldAtom = formAtom(baseAtom, validationSchema);
            const id = getTagId(tag);
            return {
                kind: "Tag",
                id,
                name,
                validationSchema,
                fieldAtom,
                disabled: disabledIds.includes(id),
            };
        });

        // A row to add a new tag
        records.unshift({
            kind: "New",
            id: addNewFieldAtom.toString(),
            validationSchema: addNewValidationSchema,
            fieldAtom: addNewFieldAtom,
        });

        return records;
    }, [
        tags,
        tagNames,
        addNewValidationSchema,
        addNewFieldAtom,
        getTagName,
        getTagId,
        disabledIds,
        onRename,
        editingIdAtom,
    ]);

    const columns: ColumnsType<DataRecord<TagId>> = React.useMemo(() => {
        return [
            {
                title: t("TagManagementTable_Column_Name"),
                dataIndex: "name",
                key: "name",
                render: (_: unknown, record: DataRecord<TagId>) => {
                    return (
                        <Name record={record} editingIdAtom={editingIdAtom} />
                    );
                },
            },
            {
                title: t("TagManagementTable_Column_Action"),
                dataIndex: "action",
                key: "action",
                className: theme.actionColumn,
                render: (_: unknown, record: DataRecord<TagId>) => {
                    return (
                        <Action
                            record={record}
                            editingIdAtom={editingIdAtom}
                            onDelete={onDelete}
                            onRename={onRename}
                            onAdd={onAdd}
                            askConfirmationOnDelete={askConfirmationOnDelete}
                            resetNew={() => {
                                resetNew();
                            }}
                        />
                    );
                },
            },
        ];
    }, [
        editingIdAtom,
        onRename,
        onAdd,
        onDelete,
        askConfirmationOnDelete,
        resetNew,
    ]);

    return (
        <Table
            dataSource={dataRecords}
            columns={columns}
            pagination={false}
            rowKey="id"
        />
    );
}

// Name
//
interface NameProps<TagId> {
    record: DataRecord<TagId>;
    editingIdAtom: PrimitiveAtom<TagId | null>;
}

function Name<TagId>(props: NameProps<TagId>): React.ReactElement {
    const { record, editingIdAtom } = props;

    const editingId = useAtomValue(editingIdAtom);

    switch (record.kind) {
        case "Tag": {
            return <NameCell record={record} editingIdAtom={editingIdAtom} />;
        }
        case "New": {
            const disabled = editingId !== null;
            return (
                <NameCellNew fieldAtom={record.fieldAtom} disabled={disabled} />
            );
        }
        default: {
            return assertNever(record);
        }
    }
}

interface NameCellProps<TagId> {
    record: DataRecordTag<TagId>;
    editingIdAtom: PrimitiveAtom<TagId | null>;
}

function NameCell<TagId>(props: NameCellProps<TagId>): React.ReactElement {
    const { record, editingIdAtom } = props;
    const editingId = useAtomValue(editingIdAtom);
    const rowState = detectRowState(record, editingId);

    switch (rowState) {
        case "Display":
        case "Disabled": {
            return <>{record.name}</>;
        }
        case "Editing": {
            return (
                <NameCellEditing
                    editingIdAtom={editingIdAtom}
                    record={record}
                />
            );
        }
        default: {
            return assertNever(rowState);
        }
    }
}

interface NameCellEditingProps<TagId> {
    editingIdAtom: PrimitiveAtom<TagId | null>;
    record: DataRecordTag<TagId>;
}

function NameCellEditing<TagId>(
    props: NameCellEditingProps<TagId>
): React.ReactElement {
    const { record } = props;

    return (
        <FAStringInput
            atom={record.fieldAtom}
            dispatch="onPressEnter"
            autoFocus
        />
    );
}

interface NameCellNewProps {
    fieldAtom: FormAtom<string>;
    disabled: boolean;
}

const NameCellNew: React.FC<NameCellNewProps> = (props: NameCellNewProps) => {
    const { fieldAtom, disabled } = props;

    return (
        <FAStringInput
            atom={fieldAtom}
            disabled={disabled}
            autoFocus
            dispatch="onPressEnter"
            placeholder={t("TagManagementTable_Add_Placeholder")}
        />
    );
};

// Action
//
interface ActionProps<TagId> {
    record: DataRecord<TagId>;
    editingIdAtom: PrimitiveAtom<TagId | null>;
    onDelete: (tagId: TagId) => void;
    onRename: (tagId: TagId, newName: string) => void;
    onAdd: (newName: string) => void;
    askConfirmationOnDelete: boolean;
    resetNew: () => void;
}

function Action<TagId>(props: ActionProps<TagId>): React.ReactElement {
    const {
        record,
        editingIdAtom,
        onDelete,
        onRename,
        askConfirmationOnDelete,
        resetNew,
    } = props;

    const { value, setCacheValue, dispatchValue } = useFormAtom(
        record.fieldAtom
    );
    const [editingId, setEditingKey] = useAtom(editingIdAtom);

    switch (record.kind) {
        case "Tag": {
            const rowState = detectRowState(record, editingId);
            const handleDelete = () => {
                onDelete(record.id);
            };
            const onSave = () => {
                if (record.validationSchema.isValidSync(value)) {
                    // Fire callback if the value actually was really changed
                    if (value !== record.name) {
                        onRename(record.id, value);
                    }
                    setEditingKey(null);
                }
            };
            const handleStartEditing = () => {
                setEditingKey(record.id);
            };
            const onCancel = () => {
                setCacheValue(record.name);
                setEditingKey(null);
            };
            return (
                <ActionCell
                    rowState={rowState}
                    onRename={handleStartEditing}
                    onDelete={handleDelete}
                    onSave={onSave}
                    onCancel={onCancel}
                    askConfirmationOnDelete={askConfirmationOnDelete}
                    tagName={record.name}
                    resetNew={resetNew}
                    disabled={record.disabled}
                />
            );
        }
        case "New": {
            const disabled = editingId !== null;
            const handleAdd = () => {
                dispatchValue(value);
                if (record.validationSchema.isValidSync(value)) {
                    setEditingKey(null);
                }
            };
            return <ActionCellNew onAdd={handleAdd} disabled={disabled} />;
        }
        default: {
            return assertNever(record);
        }
    }
}

interface ActionCellProps {
    rowState: RowState;
    onSave: () => void;
    onCancel: () => void;
    onRename: () => void;
    onDelete: () => void;
    askConfirmationOnDelete: boolean;
    tagName: string;
    resetNew: () => void;
    disabled: boolean;
}

const ActionCell: React.FC<ActionCellProps> = (props: ActionCellProps) => {
    const {
        rowState,
        onSave,
        onCancel,
        onRename,
        onDelete,
        askConfirmationOnDelete,
        tagName,
        resetNew,
        disabled,
    } = props;

    switch (rowState) {
        case "Display":
        case "Disabled": {
            return (
                <ActionCellDisplay
                    onRename={onRename}
                    onDelete={onDelete}
                    disabled={rowState === "Disabled" || disabled}
                    askConfirmationOnDelete={askConfirmationOnDelete}
                    tagName={tagName}
                    resetNew={resetNew}
                />
            );
        }
        case "Editing": {
            return <ActionCellEditing onSave={onSave} onCancel={onCancel} />;
        }
        default: {
            return assertNever(rowState);
        }
    }
};

interface ActionCellEditingProps {
    onSave: () => void;
    onCancel: () => void;
}

const ActionCellEditing: React.FC<ActionCellEditingProps> = (
    props: ActionCellEditingProps
) => {
    return (
        <>
            <Typography.Link
                onClick={props.onSave}
                className={theme.actionLeftLink}
            >
                {t("TagManagementTable_Action_Save")}
            </Typography.Link>
            <Typography.Link onClick={props.onCancel}>
                {t("TagManagementTable_Action_Cancel")}
            </Typography.Link>
        </>
    );
};

interface ActionCellDisplayProps {
    onRename: () => void;
    onDelete: () => void;
    disabled: boolean;
    askConfirmationOnDelete: boolean;
    tagName: string;
    resetNew: () => void;
}

const ActionCellDisplay: React.FC<ActionCellDisplayProps> = (
    props: ActionCellDisplayProps
) => {
    const {
        onRename,
        onDelete,
        disabled,
        askConfirmationOnDelete,
        tagName,
        resetNew,
    } = props;

    const onStartEditing = React.useCallback(() => {
        resetNew();
        onRename();
    }, [resetNew, onRename]);

    const deleteLink: React.ReactElement = React.useMemo(() => {
        if (askConfirmationOnDelete) {
            return (
                <Popconfirm
                    title={t("TagManagementTable_DeleteConfirmation_Title", {
                        tagName,
                    })}
                    onConfirm={onDelete}
                    okText={t("TagManagementTable_DeleteConfirmation_Yes")}
                    cancelText={t("TagManagementTable_DeleteConfirmation_No")}
                    placement="topRight"
                >
                    <Typography.Link disabled={disabled} onClick={resetNew}>
                        {t("TagManagementTable_Action_Delete")}
                    </Typography.Link>
                </Popconfirm>
            );
        } else {
            return (
                <Typography.Link disabled={disabled} onClick={onDelete}>
                    {t("TagManagementTable_Action_Delete")}
                </Typography.Link>
            );
        }
    }, [tagName, onDelete, disabled, askConfirmationOnDelete, resetNew]);

    return (
        <>
            <Typography.Link
                onClick={onStartEditing}
                disabled={disabled}
                className={theme.actionLeftLink}
            >
                {t("TagManagementTable_Action_Rename")}
            </Typography.Link>
            {deleteLink}
        </>
    );
};

interface ActionCellNewProps {
    onAdd: () => void;
    disabled: boolean;
}

const ActionCellNew: React.FC<ActionCellNewProps> = (
    props: ActionCellNewProps
) => {
    const { onAdd, disabled } = props;
    return (
        <>
            <Typography.Link
                onClick={onAdd}
                disabled={disabled}
                className={theme.addLink}
            >
                {t("TagManagementTable_Action_Add")}
            </Typography.Link>
        </>
    );
};

// Build a validation schema for a particular row in the table.
// The own tag name of the row needs to be excluded.
function buildValidationSchema(
    existingTagNames: Array<string>,
    ownName: string
): StringSchema<string> {
    // Exclude the own name, to allow renaming like "chess" -> "Chess".
    const lcNames = existingTagNames
        .filter((t) => t !== ownName)
        .map((t) => t.toLowerCase());

    return string()
        .transform((value, _originalValue) => {
            return value.trim();
        })
        .required(t("TagManagementTable_Error_CannotBeBlank"))
        .max(MAX_TAG_NAME_LENGTH, t("TagManagementTable_Error_TooLong"))
        .test(
            "already-exists",
            (_d) => t("TagManagementTable_Error_AlreadyExists"),
            (value) =>
                value !== undefined && !lcNames.includes(value.toLowerCase())
        );
}

export type { TagManagementTableProps };
export { MAX_TAG_NAME_LENGTH, TagManagementTable };
